/**
 * TranscriptMessage — provider-neutral record of one message in a Trial.
 *
 * Captures user inputs, assistant outputs, tool calls, and tool results
 * without coupling to any specific AI provider (Anthropic, OpenAI, etc.).
 *
 * The existing NodeMessage type (in event.ts) is per-message within a single
 * node execution. TranscriptMessage adds execution context (nodeKey) and is
 * designed to represent the full ordered transcript across all nodes in a
 * Trial, suitable for consumption by graders and for storage on Trial records.
 *
 * Adapters convert between TranscriptMessage and provider-specific formats
 * (e.g., Anthropic MessageParam[]) — see eval-harness/src/transcript.ts.
 */
import { z } from 'zod';
export const TranscriptToolCallSchema = z.object({
    id: z.string(),
    name: z.string(),
    args: z.unknown(),
});
export const TranscriptMessageSchema = z.object({
    /** Message role. Superset of standard roles to support reasoning traces. */
    role: z.enum(['system', 'user', 'assistant', 'tool', 'reasoning']),
    /** Message body. Markdown-formatted for assistant messages; raw for tool results.
     *  Always a string — empty string for pure tool-call assistant messages that have
     *  no text content (assembleTranscript uses msg.content ?? '' during conversion). */
    content: z.string(),
    /** Optional sender label (e.g., tool name for tool results). */
    name: z.string().optional(),
    /** For role='tool': the tool_call_id this result responds to. */
    toolCallId: z.string().optional(),
    /** For role='assistant': tool calls the assistant requested. */
    toolCalls: z.array(TranscriptToolCallSchema).optional(),
    /** ISO timestamp of when this message was produced. */
    timestamp: z.string().optional(),
    /** Which workflow node produced this message (null for top-level / input). */
    nodeKey: z.string().optional(),
});
//# sourceMappingURL=transcript.js.map